import { useTranslation } from 'react-i18next';

interface AICard {
  icon: string;
  titleKey: string;
  descKey: string;
  imageUrl: string;
  imageAlt: string;
  featured?: boolean;
}

export default function AISection() {
  const { t } = useTranslation();

  const topRow: AICard[] = [
    {
      icon: 'ri-robot-2-line',
      titleKey: 'ai.assistant',
      descKey: 'ai.assistant_desc',
      imageUrl: 'https://readdy.ai/api/search-image?query=Futuristic%20AI%20chatbot%20interface%20on%20dark%20background%20with%20glowing%20amber%20accent%20highlights%2C%20conversational%20UI%20with%20text%20bubbles%2C%20minimalist%20tech%20aesthetic%2C%20warm%20golden%20tones%20against%20dark%20surface%2C%20clean%20modern%20design%2C%20abstract%20digital%20background&width=800&height=600&seq=ai-assistant&orientation=landscape',
      imageAlt: 'AI Sales Assistant',
      featured: true,
    },
    {
      icon: 'ri-filter-3-line',
      titleKey: 'ai.qualification',
      descKey: 'ai.qualification_desc',
      imageUrl: 'https://readdy.ai/api/search-image?query=Lead%20scoring%20analytics%20dashboard%20displaying%20customer%20qualification%20data%20with%20charts%20and%20metrics%2C%20warm%20neutral%20colors%2C%20clean%20minimalist%20interface%20design%2C%20professional%20tech%20aesthetic%2C%20soft%20natural%20lighting&width=560&height=560&seq=ai-qualify&orientation=squarish',
      imageAlt: 'AI Lead Qualification',
    },
    {
      icon: 'ri-presentation-line',
      titleKey: 'ai.demo',
      descKey: 'ai.demo_desc',
      imageUrl: 'https://readdy.ai/api/search-image?query=Automated%20product%20demo%20generation%20interface%20showing%20personalized%20ERP%20screens%20for%20textile%20industry%2C%20warm%20clean%20design%2C%20minimalist%20UI%20elements%2C%20professional%20software%20aesthetic%2C%20soft%20ambient%20light&width=560&height=560&seq=ai-demo&orientation=squarish',
      imageAlt: 'AI Demo Generator',
    },
  ];

  const bottomRow: AICard[] = [
    {
      icon: 'ri-book-open-line',
      titleKey: 'ai.knowledge',
      descKey: 'ai.knowledge_desc',
      imageUrl: 'https://readdy.ai/api/search-image?query=Semantic%20search%20interface%20with%20knowledge%20base%20results%20displayed%20on%20screen%2C%20warm%20amber%20accents%2C%20clean%20minimalist%20UI%2C%20professional%20tech%20workspace%2C%20soft%20natural%20lighting%2C%20modern%20office%20environment&width=720&height=480&seq=ai-knowledge&orientation=landscape',
      imageAlt: 'AI Knowledge Base',
    },
    {
      icon: 'ri-customer-service-2-line',
      titleKey: 'ai.support',
      descKey: 'ai.support_desc',
      imageUrl: 'https://readdy.ai/api/search-image?query=AI%20customer%20support%20chat%20interface%20with%20automated%20resolution%20metrics%2C%20warm%20friendly%20design%2C%20clean%20minimalist%20UI%2C%20professional%20tech%20aesthetic%2C%20soft%20ambient%20lighting&width=720&height=480&seq=ai-support&orientation=landscape',
      imageAlt: 'AI Customer Support',
    },
  ];

  return (
    <section id="ai-features" className="bg-background-50">
      <div className="w-full px-4 md:px-6 lg:px-10 py-16 md:py-24">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 md:gap-6 mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight tracking-tight text-foreground-950 shrink-0">
            {t('ai.title')}
            <br />
            <span className="text-accent-500">{t('ai.title_line2')}</span>
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-md lg:text-right leading-relaxed">
            {t('ai.subtitle')}
          </p>
        </div>

        {/* Top Row — 3 Cards with featured dark card */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 mb-4 md:mb-5">
          {topRow.map((card) => (
            <div
              key={card.titleKey}
              className={`group rounded-xl overflow-hidden border transition-all duration-300 ${
                card.featured
                  ? 'bg-foreground-950 border-foreground-800 hover:border-primary-400/30 md:row-span-1'
                  : 'bg-white border-background-200/60 hover:border-primary-200/60'
              }`}
            >
              <div className="relative h-44 md:h-52 overflow-hidden">
                <img
                  src={card.imageUrl}
                  alt={card.imageAlt}
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className={`absolute inset-0 ${
                  card.featured
                    ? 'bg-gradient-to-t from-foreground-950 via-foreground-950/40 to-transparent'
                    : 'bg-gradient-to-t from-black/25 via-transparent to-transparent'
                }`}></div>
                {card.featured && (
                  <div className="absolute top-3 left-3 bg-foreground-950/70 backdrop-blur-sm text-background-50 text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap">
                    Analytics
                  </div>
                )}
              </div>
              <div className="p-4 md:p-5">
                <h3 className={`text-base md:text-lg font-bold mb-2 ${
                  card.featured ? 'text-background-50' : 'text-foreground-950'
                }`}>
                  {t(card.titleKey)}
                </h3>
                <p className={`text-xs md:text-sm leading-relaxed ${
                  card.featured ? 'text-background-50/65' : 'text-foreground-600'
                }`}>
                  {t(card.descKey)}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Row — 2 Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {bottomRow.map((card) => (
            <div
              key={card.titleKey}
              className="group bg-white rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-200/60 transition-all duration-300"
            >
              <div className="flex flex-col sm:flex-row">
                <div className="relative w-full sm:w-2/5 h-36 sm:h-auto overflow-hidden">
                  <img
                    src={card.imageUrl}
                    alt={card.imageAlt}
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent sm:bg-gradient-to-r sm:from-black/20 sm:to-transparent"></div>
                </div>
                <div className="p-4 md:p-5 sm:w-3/5">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center shrink-0">
                      <i className={`${card.icon} text-accent-600 text-sm`}></i>
                    </div>
                    <h3 className="text-sm md:text-base font-bold text-foreground-950">
                      {t(card.titleKey)}
                    </h3>
                  </div>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    {t(card.descKey)}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}